import React, { memo, useEffect, useRef } from 'react'
import Confetti from 'react-confetti'
import { Button } from 'transfix-ui/components/Button'
import { AlertModal } from 'transfix-ui/components/Modals/AlertModal'
import { Box, Divider, Grid, makeStyles, Typography, Theme } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useDimension } from 'apps/fleet-planner/hooks/useDimension'
import { BidResponse } from 'apps/fleet-planner/types'
import { CloseIcon, ClockIcon, CheckFilledIcon } from 'transfix-icons'
import { ModalSize } from 'transfix-ui'
import formatCurrency from 'utils/formatCurrency'
import { Location, LOCATION_VARIANTS } from './Location'

interface IBidAcceptedModalProps {
  updatedBid?: BidResponse
  isOpen: boolean
  onBookNow: () => void
  onClose: () => void
}

export const useBidAcceptedModalStyles = makeStyles((theme: Theme) => ({
  bidsRow: {
    color: brandColors.info,
    paddingBottom: '10px',
    paddingTop: '15px',
  },
  amount: {
    textAlign: 'right',
    fontSize: 15,
    marginLeft: 'auto',
  },
  divider: {
    borderColor: brandColors.coolGray1,
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  text: {
    color: brandColors.white,
    fontWeight: 600,
  },
  closeButton: {
    color: brandColors.white,
    float: 'right',
  },
  icon: {
    marginLeft: theme.spacing(4),
  },
  contentContainer: {
    margin: theme.spacing(4),
  },
  divConfettiContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
}))

const NUMBER_OF_CONFETTI_PIECES = 60

const getBidExpiredAtMinutes = (bidExpiredAt: string | null | undefined): number => {
  return Math.floor(Number(bidExpiredAt) / 60)
}

export const BidAcceptedModal = memo<IBidAcceptedModalProps>(
  ({ isOpen, onBookNow, onClose, updatedBid }) => {
    const { acceptedBidExpiredAt, amount = '' } = updatedBid || {}

    const classes = useBidAcceptedModalStyles()
    const { screenSize, setDimensionHandler } = useDimension()
    const { trackEvent } = useFMSAnalytics()
    const minutes = getBidExpiredAtMinutes(acceptedBidExpiredAt)

    const ref = useRef<HTMLDivElement>(null)
    const offsets = ref?.current?.getBoundingClientRect()
    const { height = 0, width = 0 } = offsets || {}

    // resize window event used to set a dynamic width for the 'size' ModalBase prop
    useEffect(() => {
      window.addEventListener('resize', setDimensionHandler)

      return () => {
        window.removeEventListener('resize', setDimensionHandler)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenSize])

    // Track when accepted offer is presented to a user
    useEffect(() => {
      if (isOpen) trackEvent('Bid Accepted Modal', 'VIEWED_BID_ACCEPTED_MODAL')
      // don't want to include trackEvent here, will result in reduntant events
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const bookNowHandlerButton = () => {
      trackEvent('Bid Accepted Modal', 'CLICKED_BOOK_NOW_MODAL')
      onBookNow()
    }

    return (
      <AlertModal
        dataTest='bid-accepted-modal'
        onClose={onClose}
        open={isOpen}
        size={(screenSize.dynamicWidth as unknown) as ModalSize}
        title={
          <div className={classes.divConfettiContainer} ref={ref}>
            <Confetti
              numberOfPieces={NUMBER_OF_CONFETTI_PIECES}
              width={width}
              height={height}
              colors={[brandColors.skyBlue6, brandColors.skyBlue2, brandColors.skyBlue7]}
            />
            <div className={classes.contentContainer}>
              <Box className={classes.closeButton} onClick={onClose}>
                <CloseIcon size='large' color='inherit' />
              </Box>
              <div className={classes.title}>
                <CheckFilledIcon className={classes.icon} size='xxlarge' color='skyBlue6' />
                <Typography variant='subtitle1' className={classes.text}>
                  Congratulations!
                </Typography>
                <Typography variant='h2' className={classes.text}>
                  Your bid was accepted!
                </Typography>
              </div>
            </div>
          </div>
        }>
        <Box pt={2} pb={1}>
          We’ve accepted your bid. All that’s left to do is book the load at the accepted rate.
        </Box>
        <Box pb={2}>
          <Box pb={1}>
            <Typography variant='caption'>
              <strong>Load Details</strong>
            </Typography>
          </Box>
          <Location bid={updatedBid} variant={LOCATION_VARIANTS.BIDS_MODAL} />
        </Box>
        <Divider className={classes.divider} />
        <Box className={classes.bidsRow}>
          <Grid container>
            <Grid item md={1}>
              <ClockIcon size='large' />
            </Grid>
            <Grid item md={8}>
              <span data-test='bid-accepted-modal--expiration'>
                This offer expires in {minutes} min
              </span>
            </Grid>
            <Grid item md={3} className={classes.amount}>
              <strong data-test='bid-accepted-modal--amount'>{`${formatCurrency(amount)}`}</strong>
            </Grid>
          </Grid>
        </Box>
        <Divider className={classes.divider} />
        <Box pt={2} pb={3}>
          <Button
            data-test='bid-accepted-modal--book-now-button'
            color='primary'
            fullWidth
            size='large'
            onClick={bookNowHandlerButton}>
            Accept Offer
          </Button>
        </Box>
      </AlertModal>
    )
  }
)
